import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskedLines, RiseIn } from "@/components/site/Reveal";
import PhoneMockup from "@/components/site/PhoneMockup";
import Marquee from "@/components/site/Marquee";
import { IDS } from "@/lib/testIds";

export default function Home() {
  useEffect(() => { document.title = "Countersign — A word can't be cloned. The scam-check app for families."; }, []);

  return (
    <main id="main">
      <Hero />
      <StatBand />
      <Marquee
        testId={IDS.home.marquee}
        items={[
          "A word only spoken.",
          "Never written down.",
          "Never online.",
          "Never leaked.",
        ]}
      />
      <Manifesto />
      <HowItWorks />
      <NeverDo />
      <Keychain />
    </main>
  );
}

/* ============================================================
   HERO — kinetic on-load moment
   ============================================================ */
function Hero() {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const glowYRaw  = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowOpacityRaw = useTransform(scrollYProgress, [0, 1], [1, 0.35]);
  const phoneYRaw = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const phoneRRaw = useTransform(scrollYProgress, [0, 1], [-4, 2]);
  // Freeze parallax at rest when the user prefers reduced motion.
  const glowY = reduce ? 0 : glowYRaw;
  const glowOpacity = reduce ? 1 : glowOpacityRaw;
  const phoneY = reduce ? 0 : phoneYRaw;
  const phoneR = reduce ? -4 : phoneRRaw;

  return (
    <section
      ref={heroRef}
      data-testid={IDS.home.hero}
      style={{ position: "relative", paddingTop: "clamp(48px, 6vw, 96px)", paddingBottom: "clamp(64px, 8vw, 140px)", overflow: "hidden" }}
    >
      <motion.div className="signal-lamp" style={{ y: glowY, opacity: glowOpacity }} />
      <div className="container-cs" style={{ position: "relative", zIndex: 2 }}>
        <div className="hero-grid" style={{ display: "grid", gap: "clamp(40px, 6vw, 80px)" }}>
          <div className="hero-copy">
            <MaskedLines
              className="display"
              stagger={0.13}
              delay={0.2}
              lines={[
                "AI can fake",
                "your son's",
                "voice.",
                <em key="it" style={{ fontStyle: "italic", color: "var(--accent-ink)", fontVariationSettings: '"opsz" 144, "SOFT" 30, "WONK" 0' }}>It can't fake</em>,
                "the word only",
                "your family knows.",
              ]}
            />
            <style>{`
              .hero-copy .display {
                font-size: clamp(44px, 7.4vw, 108px);
                line-height: 1.02;
                letter-spacing: -0.03em;
                font-weight: 500;
                color: var(--ink-primary);
                margin: 0;
              }
              .hero-copy .display .line-mask { white-space: nowrap; }
              @media (max-width: 640px) {
                .hero-copy .display .line-mask { white-space: normal; }
              }
              .hero-grid { grid-template-columns: minmax(0, 1fr); }
              @media (min-width: 960px) {
                .hero-grid { grid-template-columns: 1.25fr 1fr; align-items: center; }
              }
            `}</style>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="lead"
              style={{ marginTop: 32, maxWidth: "38ch" }}
            >
              Countersign is a scam-check app for families. Set one word only you know. Use it when a call feels wrong.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.25 }}
              style={{ marginTop: 36, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}
            >
              <a href="#" data-testid={IDS.home.heroCta} className="btn-amber" aria-label="Download Countersign on the App Store">
                <AppleGlyph /> Download on the App Store
              </a>
              <a href="#what" className="btn-ghost inline-link" style={{ minHeight: 56 }}>
                See how it works →
              </a>
            </motion.div>
          </div>

          <motion.div
            style={{ y: phoneY, rotate: phoneR, marginInline: "auto", width: "100%" }}
            className="hero-phone-wrap"
            data-testid={IDS.home.heroPhone}
          >
            <PhoneMockup variant="safeword" tilt={-4} eager label="Countersign — Your family's safe word screen" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function AppleGlyph() {
  return (
    <svg width="20" height="22" viewBox="0 0 20 22" fill="none" aria-hidden="true">
      <path fill="currentColor" d="M15.6 11.5c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.8-2-1.6-.2-3.1.9-3.9.9-.8 0-2.1-.9-3.4-.9C4.9 5.7 3.2 6.7 2.3 8.4c-1.8 3.1-.5 7.7 1.3 10.2.9 1.3 1.9 2.7 3.3 2.6 1.3-.1 1.8-.9 3.4-.9 1.6 0 2 .9 3.4.9 1.4 0 2.3-1.3 3.2-2.6 1-1.5 1.4-3 1.4-3.1-.1 0-2.7-1-2.7-4zM13.2 3.9c.7-.8 1.1-2 1-3.1-1 .1-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" />
    </svg>
  );
}

/* ============================================================
   THE CALL COMES AT 11PM — stat band
   ============================================================ */
function StatBand() {
  return (
    <section id="what" style={{ position: "relative", padding: "clamp(64px, 8vw, 120px) 0", borderTop: "1px solid var(--separator)" }}>
      <div className="container-cs">
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(40px, 5vw, 80px)" }} className="stat-grid">
          <div>
            <RiseIn>
              <div className="chapter-tag" style={{ marginBottom: 20 }}><span className="chapter-num" style={{ color: "var(--accent-ink)" }}>I</span> The call comes at 11pm</div>
            </RiseIn>
            <RiseIn delay={0.05}>
              <h2 className="font-display" style={{ fontSize: "clamp(34px, 4.4vw, 60px)", margin: "8px 0 0", maxWidth: "20ch" }}>
                It's your grandson's voice. <em style={{ color: "var(--accent-ink)" }}>It isn't him.</em>
              </h2>
            </RiseIn>
            <RiseIn delay={0.15}>
              <div className="prose-cs" style={{ marginTop: 24 }}>
                <p>Cloning a voice takes about three seconds of audio. People pull it off a TikTok, a voicemail, an Instagram video.</p>
                <p>It's your grandson's voice, crying, telling you he's in jail and asking you not to tell his mother. It isn't him. But it is his voice.</p>
              </div>
            </RiseIn>
          </div>

          <div data-testid={IDS.home.statCards} style={{ display: "grid", gap: 20 }}>
            <StatCard n="3" unit="seconds" copy="of audio is all it takes to clone a voice." />
            <StatCard n="0" unit="apps" copy="can reliably detect a cloned voice in real time." />
            <StatCard n="90" unit="%" copy="of victims could have stopped the loss by hanging up and calling back on a number they already had." />
          </div>
        </div>
      </div>
      <style>{`
        @media (min-width: 960px) {
          .stat-grid { grid-template-columns: 1fr 1fr !important; align-items: start; }
        }
      `}</style>
    </section>
  );
}

function StatCard({ n, unit, copy }) {
  return (
    <RiseIn>
      <div className="cs-card">
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <div className="font-display" style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(72px, 8vw, 120px)",
            fontWeight: 500,
            lineHeight: 0.9,
            letterSpacing: "-0.04em",
            color: "var(--ink-primary)",
            fontVariationSettings: '"opsz" 144, "SOFT" 20, "WONK" 0',
          }}>
            {n}
          </div>
          <div className="font-display" style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: "clamp(22px, 2.4vw, 34px)",
            color: "var(--accent-ink)",
            fontVariationSettings: '"opsz" 72, "SOFT" 30, "WONK" 0',
          }}>
            {unit}
          </div>
        </div>
        <p style={{ marginTop: 10, color: "var(--ink-secondary)", maxWidth: "36ch", fontSize: 18 }}>{copy}</p>
      </div>
    </RiseIn>
  );
}

/* ============================================================
   WHAT COUNTERSIGN DOES — numbered manifesto chapters
   ============================================================ */
function Manifesto() {
  // These five items are the five real App Store screenshots — verbatim
  // eyebrow headline, verbatim caption, verbatim safe-word ("Purple Pancake"),
  // verbatim button labels. If the shipped app changes, update this array;
  // do not invent copy.
  const items = [
    {
      n: "II",
      eyebrow: "WHEN IT MATTERS",
      title: "AI can fake a voice. It can't fake your family's word.",
      body: "The Home screen puts the one thing you'll reach for first — a big button to challenge a caller — above everything else. Practise the word, read the safety advice, or open the whole thing without needing to remember where anything lives.",
      variant: "home",
    },
    {
      n: "III",
      eyebrow: "STEP BY STEP",
      title: "Clear instructions when you're frightened and being rushed.",
      body: "One instruction per screen. \"Hang up now. You don't need to explain or be polite.\" One large amber button confirms you have. Nothing else on the screen to think about.",
      variant: "hangup",
    },
    {
      n: "IV",
      eyebrow: "ONE TAP AWAY",
      title: "Set your word once. It's there when the call comes.",
      body: "Your family's safe word, in giant type. Ask them to say it. Then tap \"They said it\" or \"They didn't\" — whichever it was. The example above is Purple Pancake; yours is whatever you and your family agreed on, in person.",
      variant: "safeword",
    },
    {
      n: "V",
      eyebrow: "NOT YOUR FAULT",
      title: "Because shame is why most of this goes unreported.",
      body: "When it's over, Countersign says the two things people rarely hear: that hanging up and calling back on a number you already had is what stops this working, and that nothing that just happened was your fault.",
      variant: "notfault",
    },
    {
      n: "VI",
      eyebrow: "ALWAYS FREE",
      title: "Practical advice that works whether you subscribe or not.",
      body: "\"How to stay safe\" is a short, plain-language guide. It starts with the biggest single win — sending unknown numbers straight to voicemail — and walks you through the iPhone settings that do it. None of this needs the app. It's free forever.",
      variant: "staysafe",
    },
  ];

  return (
    <section
      data-testid={IDS.home.manifesto}
      style={{ position: "relative", padding: "clamp(80px, 10vw, 160px) 0", background: "var(--surface-secondary)", borderTop: "1px solid var(--separator)", borderBottom: "1px solid var(--separator)" }}
    >
      <div className="container-cs">
        <RiseIn>
          <div className="chapter-tag" style={{ marginBottom: 20 }}><span className="dot" /> What Countersign does</div>
        </RiseIn>
        <RiseIn delay={0.05}>
          <h2 className="font-display" style={{ fontSize: "clamp(36px, 5vw, 72px)", margin: 0, maxWidth: "18ch" }}>
            Five screens. Each one for a moment that matters.
          </h2>
        </RiseIn>

        <div style={{ display: "grid", gap: "clamp(64px, 8vw, 120px)", marginTop: "clamp(56px, 6vw, 96px)" }} data-testid={IDS.home.features}>
          {items.map((it, i) => (
            <ManifestoRow key={it.n} item={it} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ManifestoRow({ item, reverse }) {
  return (
    <div className="manifesto-row" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(32px, 5vw, 72px)", alignItems: "center" }}>
      <div style={{ order: reverse ? 2 : 1 }}>
        <RiseIn>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
            <span className="chapter-num">{item.n}</span>
            <span
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 14,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: "var(--accent-ink)",
              }}
            >
              {item.eyebrow}
            </span>
          </div>
        </RiseIn>
        <RiseIn delay={0.05}>
          <h3 className="font-display" style={{ fontSize: "clamp(30px, 3.6vw, 52px)", margin: 0, maxWidth: "20ch" }}>
            {item.title}
          </h3>
        </RiseIn>
        <RiseIn delay={0.1}>
          <div className="prose-cs" style={{ marginTop: 20 }}>
            <p>{item.body}</p>
          </div>
        </RiseIn>
      </div>
      <div style={{ order: reverse ? 1 : 2, maxWidth: 340, width: "100%", marginInline: "auto" }}>
        <PhoneMockup variant={item.variant} tilt={reverse ? 3 : -3} label={`Countersign — ${item.eyebrow}`} />
      </div>
      <style>{`
        @media (min-width: 960px) {
          .manifesto-row { grid-template-columns: 1.1fr 0.9fr !important; }
        }
      `}</style>
    </div>
  );
}

/* ============================================================
   HOW IT WORKS IN PRACTICE — editorial short story
   ============================================================ */
function HowItWorks() {
  return (
    <section
      style={{
        position: "relative",
        padding: "clamp(96px, 12vw, 180px) 0",
        borderTop: "1px solid var(--separator)",
        overflow: "hidden",
      }}
    >
      <div className="signal-lamp" style={{ right: "auto", left: "-30%", top: "-10%", opacity: 0.45 }} />
      <div className="container-cs" style={{ position: "relative", zIndex: 1 }}>
        <div className="hiw-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(40px, 6vw, 96px)", alignItems: "start" }}>
          {/* left: numeral + title */}
          <div>
            <RiseIn>
              <div className="chapter-tag" style={{ marginBottom: 22 }}>
                <span className="chapter-num" style={{ color: "var(--accent-ink)" }}>VII</span> How it works in practice
              </div>
            </RiseIn>
            <RiseIn delay={0.05}>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(36px, 5vw, 72px)",
                  lineHeight: 1.02,
                  letterSpacing: "-0.03em",
                  margin: 0,
                  maxWidth: "12ch",
                }}
              >
                A cup of tea. <em style={{ color: "var(--accent-ink)" }}>One word.</em>
              </h2>
            </RiseIn>
            <RiseIn delay={0.12}>
              <p style={{ marginTop: 28, color: "var(--ink-tertiary)", fontSize: 15, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>
                <span aria-hidden="true" style={{ display: "inline-block", width: 28, height: 1, background: "currentColor", verticalAlign: "middle", marginRight: 12, opacity: 0.6 }} />
                A small story
              </p>
            </RiseIn>
          </div>

          {/* right: story paragraphs */}
          <div className="hiw-story">
            <StoryBeat
              n="one"
              delay={0}
              body={
                <>
                  <p>You and your mum agree on a word. Not a birthday, not a pet's name, nothing anyone could look up. Two words that don't belong together work best. <em style={{ color: "var(--accent-ink)", fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 500, fontSize: "1.15em", letterSpacing: "-0.01em" }}>Purple pancake.</em></p>
                </>
              }
            />
            <StoryBeat
              n="two"
              delay={0.05}
              body={<p>You agree it in person, over a cup of tea, and neither of you writes it down.</p>}
            />
            <StoryBeat
              n="three"
              delay={0.1}
              body={<p>Months later her phone rings. It's your voice, upset, asking for money. <strong>She asks for the word.</strong></p>}
            />
            <StoryBeat
              n="four"
              delay={0.15}
              body={
                <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(24px, 2.4vw, 34px)", lineHeight: 1.35, color: "var(--ink-primary)", letterSpacing: "-0.01em", fontVariationSettings: '"opsz" 72, "SOFT" 30, "WONK" 0' }}>
                  Whoever is on the other end doesn't have it.
                </p>
              }
              last
            />
          </div>
        </div>
      </div>
      <style>{`
        @media (min-width: 960px) {
          .hiw-grid { grid-template-columns: 0.9fr 1.1fr !important; }
        }
      `}</style>
    </section>
  );
}

function StoryBeat({ n, body, last, delay = 0 }) {
  return (
    <RiseIn delay={delay}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "68px 1fr",
          gap: "clamp(18px, 2vw, 32px)",
          alignItems: "start",
          paddingBottom: last ? 0 : "clamp(28px, 3vw, 44px)",
          marginBottom: last ? 0 : "clamp(28px, 3vw, 44px)",
          borderBottom: last ? "0" : "1px solid var(--separator)",
        }}
      >
        <div
          className="font-display"
          aria-hidden="true"
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 18,
            letterSpacing: "0.02em",
            color: "var(--accent-ink)",
            fontVariationSettings: '"opsz" 72, "SOFT" 30, "WONK" 0',
            paddingTop: 6,
          }}
        >
          {n}.
        </div>
        <div className="prose-cs" style={{ margin: 0 }}>{body}</div>
      </div>
    </RiseIn>
  );
}

/* ============================================================
   WHAT COUNTERSIGN WILL NEVER DO
   ============================================================ */
function NeverDo() {
  return (
    <section
      data-testid={IDS.home.neverDo}
      style={{ padding: "clamp(80px, 10vw, 160px) 0", position: "relative", overflow: "hidden" }}
    >
      <div className="signal-lamp" style={{ right: "auto", left: "-20%", top: "-10%", opacity: 0.5 }} />
      <div className="container-cs" style={{ position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(28px, 4vw, 60px)" }} className="never-grid">
          <div>
            <RiseIn>
              <div className="chapter-tag" style={{ marginBottom: 20 }}><span className="chapter-num" style={{ color: "var(--accent-ink)" }}>VIII</span> What Countersign will never do</div>
            </RiseIn>
            <RiseIn delay={0.05}>
              <h2 className="font-display" style={{ fontSize: "clamp(34px, 4.6vw, 64px)", margin: 0, maxWidth: "18ch" }}>
                Never tell you whether something was <em style={{ color: "var(--accent-ink)" }}>made by AI.</em>
              </h2>
            </RiseIn>
          </div>
          <RiseIn delay={0.1}>
            <div className="prose-cs">
              <p>Text detectors are unreliable enough that OpenAI retired its own. Image forensics break every time a new model ships, and they break anyway the moment someone screenshots a picture. An app that tells you something is <strong>"87% likely fake"</strong> is guessing, and the first time it guesses wrong in the reassuring direction, somebody loses their savings because an app told them it was fine.</p>
              <p>The safe word works for the opposite reason. It's information that only exists between people who trust each other, and it was never online for anything to learn from.</p>
            </div>
          </RiseIn>
        </div>
      </div>
      <style>{`
        @media (min-width: 960px) {
          .never-grid { grid-template-columns: 1fr 1fr !important; align-items: start; }
        }
      `}</style>
    </section>
  );
}

/* ============================================================
   KEYCHAIN — closing calm
   ============================================================ */
function Keychain() {
  return (
    <section data-testid={IDS.home.keychain} style={{ padding: "clamp(80px, 10vw, 160px) 0", background: "var(--surface-sunken)", borderTop: "1px solid var(--separator)" }}>
      <div className="container-cs" style={{ maxWidth: 980 }}>
        <RiseIn>
          <div className="chapter-tag" style={{ marginBottom: 24 }}><span className="chapter-num" style={{ color: "var(--accent-ink)" }}>IX</span> Where the word lives</div>
        </RiseIn>
        <RiseIn delay={0.05}>
          <h2 className="font-display" style={{ fontSize: "clamp(38px, 5.6vw, 84px)", margin: 0, letterSpacing: "-0.03em", lineHeight: 1.02 }}>
            Your safe word stays on your phone.
          </h2>
        </RiseIn>
        <RiseIn delay={0.1}>
          <div className="prose-cs" style={{ marginTop: 28 }}>
            <p>Your safe word is stored in your device's Keychain. It is never sent to us, because a word you send over the internet isn't a secret any more.</p>
          </div>
        </RiseIn>

        <RiseIn delay={0.15}>
          <div style={{ marginTop: 40, display: "flex", flexWrap: "wrap", gap: 14 }}>
            <a href="#" className="btn-amber" aria-label="Download Countersign on the App Store">
              <AppleGlyph /> Download on the App Store
            </a>
            <a href="/support" className="btn-ghost">Read the FAQ</a>
          </div>
        </RiseIn>
      </div>
    </section>
  );
}
